import { extractWorkspaceId } from "@/shared/lib/workspace/extractWorkspaceId";
import { useAdsWorkspaceStore } from "../../store/useAdsWorkspaceStore";

export const WORKFLOW_SLUG = "growth-copilot";
export const EXPLORE_PATH = "/explore";
export const STATIC_ADS_PATH = "/static-ads";
export const STATIC_ADCLONE_PATH = "/static-ads/adclone";
export const VIDEO_ADS_PATH = "/video-ads";
export const PAID_CAMPAIGNS_PATH = "/paid-campaigns";
export const EXPLORE_CARD_SPOTLIGHT_HOLD_MS = 7000;
export const VOICE_INTRO_TIMEOUT_MS = 34_000;
export const VOICE_INTRO_START_FALLBACK_MS = 4500;
export const STATIC_ADCLONE_OPEN_DELAY_MS = 350;
export const STATIC_ADCLONE_TARGET_TIMEOUT_MS = 10_000;

export type RunPhase = "idle" | "explore" | "waiting_for_next" | "continuing";
export type OnboardingStage =
  | "explore"
  | "image_ads"
  | "video_ads"
  | "meta_campaigns"
  | "agents_list"
  | "static_adclone";

function onboardingControlPrompt(marker: string, stage: OnboardingStage): string {
  return `[${marker}]
Internal onboarding control. Call only marcus_show_onboarding_step with stage="${stage}". The tool and frontend sequencer own the avatar narration; after the tool returns, send only a terse transition/status line.`;
}

export const START_PROMPT = onboardingControlPrompt("MARCUS_ONBOARDING_V1_START", "explore");

export const CONTINUE_PROMPT = onboardingControlPrompt(
  "MARCUS_ONBOARDING_V1_CONTINUE",
  "image_ads",
);

export const CONTINUE_VIDEO_PROMPT = onboardingControlPrompt(
  "MARCUS_ONBOARDING_V1_VIDEO",
  "video_ads",
);

export const CONTINUE_META_PROMPT = onboardingControlPrompt(
  "MARCUS_ONBOARDING_V1_META",
  "meta_campaigns",
);

export const CONTINUE_AGENTS_LIST_PROMPT = onboardingControlPrompt(
  "MARCUS_ONBOARDING_V1_AGENTS_LIST",
  "agents_list",
);

function highlightSelector(target: string): string {
  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(target)
      : target.replace(/"/g, '\\"');
  return `[data-highlight-id="${escaped}"]`;
}

export function routeWithWorkspace(route: string): string {
  if (typeof window === "undefined") return route;
  const workspaceId =
    extractWorkspaceId(window.location.pathname, window.location.search) ??
    useAdsWorkspaceStore.getState().scopeWorkspaceId;
  if (!workspaceId) return route;
  try {
    const url = new URL(route, window.location.origin);
    if (!url.searchParams.has("ws")) url.searchParams.set("ws", workspaceId);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return route;
  }
}

export async function waitForHighlightTarget(
  target: string,
  timeoutMs: number,
): Promise<boolean> {
  if (typeof document === "undefined") return false;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (document.querySelector(highlightSelector(target))) return true;
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  return false;
}

export function inferOnboardingStageFromPath(pathname: string): OnboardingStage | null {
  if (pathname.startsWith(EXPLORE_PATH)) return "explore";
  if (pathname.startsWith(STATIC_ADS_PATH)) return "image_ads";
  if (pathname.startsWith(VIDEO_ADS_PATH)) return "video_ads";
  if (pathname.startsWith(PAID_CAMPAIGNS_PATH)) return "meta_campaigns";
  return null;
}
